/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import debounce from 'lodash/debounce';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { Platform, View, ViewStyle, useWindowDimensions } from 'react-native';
import { format, startOfDay } from 'date-fns';

import TextFieldView from '@airasia-phoenix/react-native/components/TextFieldView';
import { TextFieldRefs } from '@airasia-phoenix/react-native/components/TextFieldView/types';
import {
  checkDatesEqual,
  getMonthsBetweenDates,
} from '@airasia-phoenix/react-native/utils/dateUtils';
import { getColor, pd } from '@airasia-phoenix/react-native/utils/utils';
import DatePickerMenu from '@airasia-phoenix/react-native/widgets/DatePickerView/DatePickerMenu';
import DatePickerWithBottomSheet from '@airasia-phoenix/react-native/widgets/DatePickerView/DatePickerWithBottomSheet';
import DatePickerWithCenterSheet from '@airasia-phoenix/react-native/widgets/DatePickerView/DatePickerWithCenterSheet';
import {
  DatePickerWithBottomSheetRefs,
  DatePickerWithCenterSheetRefs,
  MonthPriceObject,
} from '@airasia-phoenix/react-native/widgets/DatePickerView/types';
import { useIsLoggedIn, useAccessToken } from '@airasia-rn/account/hooks';
import { useLogger } from '@airasia-rn/core-logger';
import { useAppConfig, useDeviceInfo } from '@airasia-rn/ui';
import { getCurrentBreakpoint } from '@airasia-rn/ui/utils';

import { AdditionalLabels, JOURNEY_TYPE } from '../../config/constants';
import Constants from './constants';
import { getDateAfterSeconds } from '@airasia-phoenix/react-native/utils/dateUtils';

import { useInsertParamsInUrl } from '../../hooks/flights';
import { useAuthToken } from '../../hooks/useAuth';
import { useLowFareMonths } from '../../hooks/useLowFareMonth';
import { useDesktopWebHover } from '../../hooks/useDesktopWebHover';

import { StationDataObject } from '../../types/FlightSearch.types';
import { QueryType } from '../../types/lowFare.types';
import { same_station_object } from '../../util/FlightSearch.utils';
import { TripType } from '../TripTypeSelector/types';

import { FlightSearchEvent, FlightSearchMeta } from '../../analytics';
import { CalendarProps, FareLoadedMap } from './types';
import {
  deriveVisibleMonthsFromRanges,
  extractFareItems,
  getCalendarComparisonDate,
  get_empty_fares,
  findSelectedFarePrice,
  hasMonthPriceObjectChanged,
  normalizeApiDateToCalendarKey,
  shouldCommitVisibleMonths,
  toMonthKey,
  validateDatesBeforeConfirm,
} from './utils';
import MobileWebDatePickerBottomSheet from './MobileWebDatePickerBottomSheet';
import { useFlightConfig } from '../../config/FlightConfigProvider';
import { usePortalUsageEnabled } from '../../hooks';
import PortalWrapperView from '../PortalWrapperView/PortalWrapperView';

const VISIBLE_MONTHS_DEBOUNCE_MS = 200;

// prevents setVisibleMonths loops
function sameArray(a: string[], b: string[]) {
  if (a === b) return true;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
  return true;
}

const isScrollableElement = (element: HTMLElement) => {
  const overflowY = window.getComputedStyle(element).overflowY;

  return (
    (overflowY === 'auto' ||
      overflowY === 'scroll' ||
      overflowY === 'overlay') &&
    element.scrollHeight > element.clientHeight
  );
};

type AnchorLayout = {
  x: number;
  y: number;
  width: number;
  height: number;
};

// Single-column month list at md; fits the narrowest md viewport (768).
const CENTER_SHEET_WIDTH = 360;

const CalendarView = (props: CalendarProps) => {
  const { height } = useWindowDimensions();
  const [fares, setFares] = useState(
    get_empty_fares(Constants.CALENDAR_START_DATE, Constants.CALENDAR_END_DATE),
  );
  const [visibleMonths, setVisibleMonths] = useState<string[]>([]);
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [renderCount, setRenderCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [anchorLayout, setAnchorLayout] = useState<AnchorLayout | null>(null);
  const [calendarEndDate, setCalendarEndDate] = useState(
    Constants.CALENDAR_END_DATE,
  );
  const [localDates, setLocalDates] = useState<(Date | undefined)[]>(
    props.tripType === TripType.ONE_WAY
      ? [props.departDate]
      : [props.departDate, props.returnDate],
  );

  const selectedDate = localDates;

  const [activeField, setActiveField] = useState<'depart' | 'return'>('depart');

  const appConfig = useAppConfig();
  const flightConfig = useFlightConfig();
  const isDistress = !!flightConfig?.config?.isDistressflights;
  const { t } = useTranslation('flights');
  const { logEvent } = useLogger();

  const currentBreakpoint = getCurrentBreakpoint();
  const isWebView = currentBreakpoint > 1;
  // md only: the anchored two-month dropdown is 754px wide and the content
  // column caps at 734, so md uses the centred sheet instead.
  const isCenterSheetView = currentBreakpoint === 2;

  const loadedFaresForMonths = useRef<FareLoadedMap>({});
  const viewableItemsDates = useRef<Date[][]>([]);
  const pendingVisibleMonthKey = useRef<string | null>(null);

  const lastSelectedDates = useRef<(Date | undefined)[]>([
    props.departDate,
    props.returnDate,
  ]);
  const draftDatesRef = useRef<(Date | undefined)[]>([
    props.departDate,
    props.returnDate,
  ]);
  const didConfirmRef = useRef(false);
  const datePickerWithBottomSheetRef =
    useRef<DatePickerWithBottomSheetRefs>(null);
  const datePickerCenterSheetRef = useRef<DatePickerWithCenterSheetRefs>(null);
  const departDateTextField = useRef<TextFieldRefs>(null);
  const returnDateTextField = useRef<TextFieldRefs>(null);

  const prevOriginObj = useRef(props.originStationObject);
  const prevDestObj = useRef(props.destinationStationObject);
  const prevDepartDate = useRef(props.departDate);
  const prevReturnDate = useRef(props.returnDate);
  const dateAnchorRef = useRef<View>(null);
  const dropdownRef = useRef<View>(null);
  // const calendarScrollIndex = useRef<any>(null);
  const [calendarScrollIndex, setCalendarScrollIndex] = useState<number | null>(
    null,
  );
  const { isWeb, isMobileWidth } = useDeviceInfo();
  const mobileWebHeaderHeight = 64;
  const isMobileWebView = isWeb && isMobileWidth;
  // TextFieldView's own hover is dead when editable={false}, so drive it here (desktop web only)
  const { isHovered, hoverProps } = useDesktopWebHover();
  const datePickerBottomSheetHeight = isMobileWebView
    ? '100%'
    : (height - (isMobileWebView ? mobileWebHeaderHeight : 0)) * 0.8;
  const DatePickerBottomSheetComponent = isMobileWebView
    ? MobileWebDatePickerBottomSheet
    : DatePickerWithBottomSheet;

  const { data: authToken } = useAuthToken(props?.screenName);
  const { isLoggedIn } = useIsLoggedIn('FLIGHTS');
  const { data: accessToken } = useAccessToken('FLIGHTS');

  const effectiveAuthToken = isLoggedIn ? accessToken : authToken;

  const initialDatesRef = useRef<(Date | undefined)[]>([
    props.departDate,
    props.returnDate,
  ]);

  const storeInitialDates = useCallback(() => {
    initialDatesRef.current = [props.departDate, props.returnDate];
  }, [props.departDate, props.returnDate]);

  const haveDatesChanged = useCallback(
    (oldDates: (Date | undefined)[], newDates: (Date | undefined)[]) => {
      return (
        !checkDatesEqual(oldDates[0], newDates[0]) ||
        !checkDatesEqual(oldDates[1], newDates[1])
      );
    },
    [],
  );

  useEffect(() => {
    let shouldResetFares = false;

    if (
      !same_station_object(prevOriginObj.current, props.originStationObject)
    ) {
      loadedFaresForMonths.current = {};
      prevOriginObj.current = props.originStationObject;
      shouldResetFares = true;
    }

    if (
      !same_station_object(prevDestObj.current, props.destinationStationObject)
    ) {
      loadedFaresForMonths.current = {};
      prevDestObj.current = props.destinationStationObject;
      shouldResetFares = true;
    }

    if (!checkDatesEqual(prevDepartDate.current, props.departDate)) {
      loadedFaresForMonths.current = {};
      prevDepartDate.current = props.departDate;
    }

    if (!checkDatesEqual(prevReturnDate.current, props.returnDate)) {
      loadedFaresForMonths.current = {};
      prevReturnDate.current = props.returnDate;
    }

    if (shouldResetFares) {
      setFares(
        get_empty_fares(
          Constants.CALENDAR_START_DATE,
          Constants.CALENDAR_END_DATE,
        ),
      );
    }
  }, [
    props.originStationObject,
    props.destinationStationObject,
    props.departDate,
    props.returnDate,
  ]);

  // Synchronize internal calendar state when props change (e.g., during store hydration)
  useEffect(() => {
    const newDates =
      props.tripType === TripType.ONE_WAY
        ? [props.departDate]
        : [props.departDate, props.returnDate];

    // 1. Update the state that DatePickerMenu/BottomSheet uses for UI highlighting
    setLocalDates(newDates);

    // 2. Update refs used for the "Confirm", "Reset", and "Close" logic
    draftDatesRef.current = [props.departDate, props.returnDate];
    lastSelectedDates.current = [props.departDate, props.returnDate];

    // 3. Update tracking refs to prevent unnecessary fare/data resets
    prevDepartDate.current = props.departDate;
    prevReturnDate.current = props.returnDate;
  }, [props.departDate, props.returnDate, props.tripType]);

  const measureAnchor = useCallback(() => {
    if (!dateAnchorRef.current) return;

    // RN Web exposes DOM node here
    const el = dateAnchorRef.current as unknown as HTMLElement;
    if (!el.getBoundingClientRect) return;

    const rect = el.getBoundingClientRect();

    setAnchorLayout({
      x: rect.left,
      y: rect.top,
      width: rect.width,
      height: rect.height,
    });
  }, [setAnchorLayout]);

  useEffect(() => {
    if (props.autoOpenDatePickerMenu) {
      setActiveField('depart');
      requestAnimationFrame(() => {
        measureAnchor();
        setIsOpen(true);
        props?.setAutoOpenDatePickerMenu?.(false);
      });
    }
  }, [
    props.autoOpenDatePickerMenu,
    props.setAutoOpenDatePickerMenu,
    measureAnchor,
  ]);

  useEffect(() => {
    if (!isWebView || !isOpen) return;

    const handleReposition = () => {
      measureAnchor();
    };

    // Capture ALL scrolls (nested containers)
    window.addEventListener('scroll', handleReposition, true);
    window.addEventListener('resize', handleReposition);

    return () => {
      window.removeEventListener('scroll', handleReposition, true);
      window.removeEventListener('resize', handleReposition);
    };
  }, [isWebView, isOpen, measureAnchor]);

  const {
    tripType: outsidePressTripType,
    shouldAutoSearchOnCalendarConfirm,
    setShouldAutoSearchOnCalendarConfirm,
  } = props;

  // autoSearch is an inline arrow in the parent, so keep the latest one in a ref
  // instead of a dependency, or it re-subscribes the listener on every render.
  const autoSearchRef = useRef(props.autoSearch);
  useEffect(() => {
    autoSearchRef.current = props.autoSearch;
  });

  // Fires when a press lands outside the picker menu and its anchor. The search
  // runs here rather than on every date tap; autoSearch itself is listing-only.
  const onOutsidePress = useCallback(() => {
    const finalDates = lastSelectedDates.current;
    if (!finalDates[0]) return;

    const shouldTriggerAutoSearch =
      haveDatesChanged(initialDatesRef.current, finalDates) ||
      shouldAutoSearchOnCalendarConfirm;

    if (shouldTriggerAutoSearch) {
      autoSearchRef.current?.({
        departDate: finalDates[0],
        returnDate:
          outsidePressTripType === TripType.ONE_WAY ? undefined : finalDates[1],
      });
    }
    setShouldAutoSearchOnCalendarConfirm?.(false);
  }, [
    haveDatesChanged,
    outsidePressTripType,
    shouldAutoSearchOnCalendarConfirm,
    setShouldAutoSearchOnCalendarConfirm,
  ]);

  useEffect(() => {
    // The centred sheet is not inside dropdownRef, so every click in it would
    // read as an outside press. It handles its own dismissal.
    if (!isWebView || !isOpen || isCenterSheetView) return;

    const handlePointerDown = (e: MouseEvent) => {
      const target = e.target as Node;

      const clickedAnchor =
        dateAnchorRef.current &&
        (dateAnchorRef.current as unknown as Node).contains?.(target);

      const clickedDropdown =
        dropdownRef.current &&
        (dropdownRef.current as unknown as Node).contains?.(target);

      if (!clickedAnchor && !clickedDropdown) {
        onOutsidePress();
        setIsOpen(false);
      }
    };

    document.addEventListener('pointerdown', handlePointerDown);

    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
    };
  }, [isWebView, isOpen, isCenterSheetView, onOutsidePress]);

  useEffect(() => {
    if (isOpen) return;
    if (props.tripType === TripType.ONE_WAY) {
      setLocalDates([props.departDate]);
    } else {
      setLocalDates([props.departDate, props.returnDate]);
    }
  }, [props.tripType, props.departDate, props.returnDate, isOpen]);

  useEffect(() => {
    if (!isMobileWebView || !isDatePickerOpen) return;
    if (typeof document === 'undefined') return;

    const lockedElements: Array<{
      element: HTMLElement;
      overflow: string;
      overscrollBehavior: string;
      touchAction: string;
    }> = [];

    const lockElement = (element: HTMLElement | null) => {
      if (!element) return;
      if (lockedElements.some((item) => item.element === element)) return;

      lockedElements.push({
        element,
        overflow: element.style.overflow,
        overscrollBehavior: (element.style as CSSStyleDeclaration)
          .overscrollBehavior,
        touchAction: element.style.touchAction,
      });

      element.style.overflow = 'hidden';
      (element.style as CSSStyleDeclaration).overscrollBehavior = 'none';
      element.style.touchAction = 'none';
    };

    const root = dateAnchorRef.current as unknown as HTMLElement | null;
    let currentElement = root?.parentElement ?? null;

    while (currentElement) {
      if (isScrollableElement(currentElement)) {
        lockElement(currentElement);
      }
      currentElement = currentElement.parentElement;
    }

    lockElement(document.body);
    lockElement(document.documentElement);

    return () => {
      lockedElements.forEach(
        ({ element, overflow, overscrollBehavior, touchAction }) => {
          element.style.overflow = overflow;
          (element.style as CSSStyleDeclaration).overscrollBehavior =
            overscrollBehavior;
          element.style.touchAction = touchAction;
        },
      );
    };
  }, [isDatePickerOpen, isMobileWebView]);

  const baseQueryParams: QueryType = useMemo(() => {
    if (!props.originStationObject || !props.destinationStationObject) {
      return { departStation: '', arrivalStation: '' };
    }

    const departObj: StationDataObject = !props.departFlightSelected
      ? props.originStationObject
      : props.destinationStationObject;

    const arrivalObj: StationDataObject = !props.departFlightSelected
      ? props.destinationStationObject
      : props.originStationObject;

    return {
      departStation: departObj.StationCode,
      arrivalStation: arrivalObj.StationCode,
      currency: flightConfig?.config?.currency || appConfig?.config?.currency,
      isOriginCity: !!departObj.IsCity,
      isDestinationCity: !!arrivalObj.IsCity,
      airlineProfile: props.airlineProfile,
    };
  }, [
    props.originStationObject,
    props.destinationStationObject,
    props.departDate,
    props.returnDate,
    flightConfig?.config?.currency,
    appConfig.config.currency,
    props.departFlightSelected,
    props.airlineProfile,
  ]);

  // Skip transient viewable ranges reported while the list is still
  // scrolling into place (e.g. the default position before jumping to
  // calendarScrollIndex on open) - only fetch once viewability settles.
  const commitVisibleMonths = useMemo(
    () =>
      debounce((viewableItemsDateRanges: Date[][]) => {
        const derived = deriveVisibleMonthsFromRanges(viewableItemsDateRanges);

        if (
          !shouldCommitVisibleMonths(derived, pendingVisibleMonthKey.current)
        ) {
          return;
        }

        pendingVisibleMonthKey.current = null;
        setVisibleMonths((prev) => (sameArray(prev, derived) ? prev : derived));
        setRenderCount((curr) => curr + 1);
      }, VISIBLE_MONTHS_DEBOUNCE_MS),
    [],
  );

  const onViewableItems = useCallback(
    (viewableItemsDateRanges: Date[][]) => {
      const rangesSnapshot = viewableItemsDateRanges.map((range) => [...range]);
      const derived = deriveVisibleMonthsFromRanges(rangesSnapshot);

      if (!shouldCommitVisibleMonths(derived, pendingVisibleMonthKey.current)) {
        return;
      }

      viewableItemsDates.current = rangesSnapshot;
      commitVisibleMonths(rangesSnapshot);
    },
    [commitVisibleMonths],
  );

  useEffect(() => {
    return () => {
      commitVisibleMonths.cancel();
    };
  }, [commitVisibleMonths]);

  useEffect(() => {
    // Route through the same debounced commit as onViewableItems - this was
    // previously a direct setVisibleMonths call, which meant a change to any
    // of these deps could commit whatever viewableItemsDates.current held at
    // that exact moment (possibly a transient pre-scroll value) immediately,
    // bypassing the debounce protection entirely.
    commitVisibleMonths(viewableItemsDates.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    props.departDate,
    props.returnDate,
    renderCount,
    props.destinationStationObject,
    props.originStationObject,
  ]);

  const lowFareVisibleMonths = useMemo(
    () =>
      (isWebView && isOpen) || (!isWebView && isDatePickerOpen)
        ? visibleMonths
        : [],
    [isDatePickerOpen, isOpen, isWebView, visibleMonths],
  );

  const monthQueries = useLowFareMonths(
    lowFareVisibleMonths,
    baseQueryParams,
    effectiveAuthToken ?? '',
    isLoggedIn ? 'MEMBER' : 'ANONYMOUS',
    props?.screenName,
  );

  const insertParamsInUrl = useInsertParamsInUrl();

  // stable signature so effect doesn't run every render just because array identity changes
  const monthQueriesSignature = useMemo(
    () => monthQueries.map((q) => `${q.status}:${q.dataUpdatedAt}`),
    [monthQueries],
  );

  const { enabled: isPortalUsageEnabled } = usePortalUsageEnabled();

  const portalStyle: ViewStyle = useMemo(() => {
    if (!anchorLayout) return { opacity: 0 };

    const DROPDOWN_WIDTH = 752;
    const EDGE_PADDING = 16;
    const GAP = 3;

    const desiredLeft =
      anchorLayout.x + anchorLayout.width / 2 - DROPDOWN_WIDTH / 2;

    const left = Math.max(
      EDGE_PADDING,
      Math.min(desiredLeft, window.innerWidth - DROPDOWN_WIDTH - EDGE_PADDING),
    );

    const top = anchorLayout.y + anchorLayout.height + GAP;

    return {
      position: 'absolute',
      left,
      top,
      width: DROPDOWN_WIDTH,
      maxWidth: window.innerWidth - EDGE_PADDING * 2,
      zIndex: 22,
    };
  }, [anchorLayout]);

  const dropDownStyle: ViewStyle = useMemo(() => {
    if (!anchorLayout) return { opacity: 0 };
    const DROPDOWN_WIDTH = 752;
    const EDGE_PADDING = 16;
    const GAP = 54;

    let layoutLeft = anchorLayout.x;
    let layoutWidth = anchorLayout.width;

    // 1. Locate the precise search widget container using its ID
    if (
      typeof document !== 'undefined' &&
      Platform.OS === 'web' &&
      dateAnchorRef.current
    ) {
      const anchorEl = dateAnchorRef.current as unknown as HTMLElement;
      const widgetEl = anchorEl.closest('#search-widget-container');

      if (widgetEl) {
        const rect = widgetEl.getBoundingClientRect();
        layoutLeft = rect.left;
        layoutWidth = rect.width;
      }
    }

    // 2. Ensure we respect a minimum padding from the screen edge
    const safeLayoutLeft = layoutLeft + EDGE_PADDING;
    const safeLayoutRight = layoutLeft + layoutWidth - EDGE_PADDING;

    // 3. Translate the widget boundaries to local coordinates relative to the anchor
    const minLocalLeft = safeLayoutLeft - anchorLayout.x;
    const maxLocalLeft = safeLayoutRight - DROPDOWN_WIDTH - anchorLayout.x;

    // 4. Calculate the ideal centered offset relative to the anchor
    const localDesiredLeft = anchorLayout.width / 2 - DROPDOWN_WIDTH / 2;

    // 5. Clamp the position perfectly
    const left = Math.max(
      minLocalLeft,
      Math.min(localDesiredLeft, maxLocalLeft),
    );
    const top = GAP;

    return {
      position: 'absolute',
      left,
      top,
      width: DROPDOWN_WIDTH,
      maxWidth: window.innerWidth - EDGE_PADDING * 2,
      zIndex: 22,
    };
  }, [anchorLayout]);

  /**
   * Map hook results -> fares (idempotent, single setFares)
   */
  useEffect(() => {
    if (!monthQueries.length) return;

    setFares((currFares: any[]) => {
      let changed = false;
      const next = [...currFares];

      const comparisonDate = getCalendarComparisonDate({
        activeField,
        selectedDates: localDates,
        tripType: props.tripType,
      });
      const selectedDateObj = comparisonDate
        ? startOfDay(comparisonDate)
        : null;
      const fareGroups = monthQueries.map((query) =>
        query.isSuccess ? extractFareItems(query.data) : [],
      );
      const selectedFarePrice = findSelectedFarePrice({
        fareGroups,
        selectedDate: selectedDateObj,
        cachedFares: next,
      });

      monthQueries.forEach((q, idx) => {
        if (!q.isSuccess) return;

        const monthKey = lowFareVisibleMonths[idx];
        if (!monthKey) return;

        const items = fareGroups[idx];
        if (!items.length) return;

        const monthPriceObj: MonthPriceObject = {};

        items.forEach((fareItem) => {
          const key = normalizeApiDateToCalendarKey(fareItem.departureDate);

          monthPriceObj[key] = {
            displayPriceString: insertParamsInUrl(
              AdditionalLabels.PRICE_STRING,
              [fareItem.shortPrice],
            ),
            price: fareItem.price,
            soldOut: fareItem.soldOut ?? false,
            soldOutLabel: t('flightsSearchPage.calendar.soldOut'),
            noFlights: fareItem.noFlights ?? false,
            priceStringColor:
              typeof fareItem.price === 'number' &&
              typeof selectedFarePrice === 'number' &&
              fareItem.price < selectedFarePrice
                ? Constants.LOWER_PRICE_COLOR
                : undefined,
          };
        });

        const [y, m] = monthKey.split('-').map(Number);
        const monthEnd = new Date(y, m, 0);

        const monthIndex = getMonthsBetweenDates(
          Constants.CALENDAR_START_DATE,
          monthEnd,
        );

        const prevMonthObj = next[monthIndex] as MonthPriceObject | undefined;

        if (hasMonthPriceObjectChanged(prevMonthObj, monthPriceObj)) {
          next[monthIndex] = monthPriceObj;
          changed = true;
        }
      });

      return changed ? next : currFares;
    });
  }, [
    activeField,
    insertParamsInUrl,
    localDates,
    lowFareVisibleMonths,
    monthQueries,
    monthQueriesSignature,
    props.tripType,
    t,
  ]);

  const sendDateSelectionEvent = (
    departDate: Date | undefined,
    retDate: Date | undefined,
  ) => {
    if (!departDate || !retDate) return;

    const originDestinationPair = `${props.originStationObject?.StationCode} - ${props.destinationStationObject?.StationCode}`;
    const departureDate = format(departDate, 'dd/MM/yyyy');
    const isRoundTrip = props.tripType === TripType.ROUND_TRIP;
    const returnDate =
      retDate !== undefined ? format(retDate, 'dd/MM/yyyy') : 'undefined';
    const label = isRoundTrip
      ? `${props.tripType} | ${originDestinationPair} | ${departureDate} | ${returnDate}`
      : `${props.tripType} | ${originDestinationPair} | ${departureDate}`;
    const params = {
      label,
      category: `${FlightSearchMeta.ANALYTICS_CONSTANTS.HP_SEARCH_WIDGET}|${FlightSearchMeta.ANALYTICS_CONSTANTS.DATE_PICKER}`,
    };

    logEvent(
      FlightSearchMeta.EVENT_NAMES.click,
      FlightSearchEvent.dateSelectionEvent(params),
    );
  };

  // commits exactly like a Confirm press + some extra logic
  const persistSelectedDates = useCallback(
    (dates: (Date | undefined)[]) => {
      const isOneWay = props.tripType === TripType.ONE_WAY;

      // Only commit a complete selection. A round trip mid-selection has no
      // return date yet, and validateDatesBeforeConfirm would invent one.
      if (!dates[0]) return;
      if (!isOneWay && !dates[1]) return;

      let departDate: Date | undefined;
      let returnDate: Date | undefined;

      if (isOneWay) {
        // validateDatesBeforeConfirm invents a depart + 3 return whenever there
        // is no return date, which is never right for one way.
        departDate = dates[0];
        returnDate = undefined;
      } else {
        [departDate, returnDate] = validateDatesBeforeConfirm(
          dates[0],
          dates[1],
        );
      }

      sendDateSelectionEvent(departDate, returnDate);
      props.onChangeDepartDate(departDate);
      props.onChangeReturnDate(returnDate);

      setLocalDates([departDate, returnDate]);
      lastSelectedDates.current = [departDate, returnDate];
      // No setIsOpen(false) / toggleDatePicker() - the picker stays open.
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [props.tripType, props.onChangeDepartDate, props.onChangeReturnDate],
  );

  const handleDatePickerClose = useCallback(() => {
    setIsDatePickerOpen(false);
    pendingVisibleMonthKey.current = null;
    viewableItemsDates.current = [];
    setVisibleMonths((prev) => (prev.length ? [] : prev));
    // reset loaded fares cache
    loadedFaresForMonths.current = {};
    setCalendarScrollIndex(null);
    // reset fares state
    setFares(
      get_empty_fares(
        Constants.CALENDAR_START_DATE,
        Constants.CALENDAR_END_DATE,
      ),
    );

    // restore last selected dates
    if (!didConfirmRef.current) {
      if (props.tripType === TripType.ROUND_TRIP) {
        props.onChangeDepartDate(lastSelectedDates.current[0]);
        props.onChangeReturnDate(lastSelectedDates.current[1]);
        setLocalDates([
          lastSelectedDates.current[0],
          lastSelectedDates.current[1],
        ]);
      } else {
        props.onChangeDepartDate(lastSelectedDates.current[0]);
        setLocalDates([lastSelectedDates.current[0]]);
      }
    }
    didConfirmRef.current = false;

    // blur text fields correctly
    if (lastSelectedDates.current[0]) {
      departDateTextField.current?.blurWithText();
    } else {
      departDateTextField.current?.blur();
    }

    if (lastSelectedDates.current[1]) {
      returnDateTextField.current?.blurWithText();
    } else {
      returnDateTextField.current?.blur();
    }
  }, [props.tripType, props.onChangeDepartDate, props.onChangeReturnDate]);

  const getIndexFromDate = (targetDate: Date, startDate: Date): number => {
    if (!targetDate || !startDate) return 0;

    // 1. Calculate the difference in years and months
    const yearsDiff = targetDate.getFullYear() - startDate.getFullYear();
    const monthsDiff = targetDate.getMonth() - startDate.getMonth();

    // 2. Convert years to months (years * 12) and add the month offset
    const index = yearsDiff * 12 + monthsDiff;

    return index;
  };

  const getCalendarOpenTargetDate = useCallback(
    (nextActiveField: 'depart' | 'return') => {
      const [departDate, returnDate] = selectedDate;

      if (nextActiveField === 'return') {
        return returnDate ?? departDate ?? new Date();
      }

      return departDate ?? returnDate ?? new Date();
    },
    [selectedDate],
  );

  const getTravelDateDisplay = useCallback(() => {
    const departDate = isWebView ? localDates?.[0] : props.departDate;
    const returnDate = isWebView ? localDates?.[1] : props.returnDate;

    if (!departDate) return '';

    const formatDate = (date: Date) => {
      const day = date.toLocaleDateString('en-GB', { day: 'numeric' });
      const month = date.toLocaleDateString('en-GB', { month: 'short' });
      return `${day} ${month}`;
    };

    if (props.tripType === TripType.ONE_WAY || !returnDate) {
      return format(departDate, 'EEE, d MMM');
    }

    return `${format(departDate, 'EEE, d MMM')} - ${format(returnDate, 'EEE, d MMM')}`;
  }, [
    isWebView,
    localDates,
    props.departDate,
    props.returnDate,
    props.tripType,
  ]);

  // The centred sheet is imperative (open/close via ref) unlike the
  // declaratively rendered dropdown, so mirror isOpen onto it.
  useEffect(() => {
    if (!isCenterSheetView || !isWebView) return;
    if (isOpen) {
      datePickerCenterSheetRef.current?.open();
    } else {
      datePickerCenterSheetRef.current?.close();
    }
  }, [isCenterSheetView, isWebView, isOpen]);

  const onResetCalendar = () => {
    const now = new Date();

    const departDate = getDateAfterSeconds(
      now,
      Constants.DEFAULT_DEPART_DAYS * 24 * 60 * 60,
    );

    let newDates: (Date | undefined)[];

    if (props.tripType === TripType.ONE_WAY) {
      newDates = [departDate];
    } else {
      const returnDate = getDateAfterSeconds(
        departDate,
        Constants.DAYS_GAP_DEPART_RETURN_DATE,
      );
      newDates = [departDate, returnDate];
    }

    const index = getIndexFromDate(departDate, Constants.CALENDAR_START_DATE);

    viewableItemsDates.current = [];
    setVisibleMonths((prev) => (prev.length ? [] : prev));
    pendingVisibleMonthKey.current = toMonthKey(departDate);
    setCalendarScrollIndex(null);

    setTimeout(() => {
      setCalendarScrollIndex(index);
      draftDatesRef.current = [];
      setLocalDates([]);
    }, 0);
    setActiveField('depart');
  };

  return (
    <View
      ref={dateAnchorRef}
      style={{
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
      }}
    >
      <View style={{ flex: 1 }} {...hoverProps}>
        <TextFieldView
          highlightBorder={isWebView && (isOpen || isHovered)}
          ref={departDateTextField}
          onPress={() => {
            const currentActiveField =
              props.tripType === TripType.ROUND_TRIP &&
              props.departDate &&
              !props.returnDate
                ? 'return'
                : 'depart';
            setActiveField(currentActiveField);
            draftDatesRef.current = [props.departDate, props.returnDate];
            const openTargetDate =
              getCalendarOpenTargetDate(currentActiveField);
            const openTargetIndex = getIndexFromDate(
              openTargetDate,
              Constants.CALENDAR_START_DATE,
            );
            viewableItemsDates.current = [];
            setVisibleMonths((prev) => (prev.length ? [] : prev));
            pendingVisibleMonthKey.current = toMonthKey(openTargetDate);

            if (isWebView) {
              measureAnchor();
              storeInitialDates();
              setIsOpen(true);
              setCalendarScrollIndex(openTargetIndex);
            } else {
              setCalendarScrollIndex(null);
              if (isMobileWebView) {
                setIsDatePickerOpen(true);
              }
              setTimeout(() => {
                setCalendarScrollIndex(openTargetIndex);
              }, 0);
              datePickerWithBottomSheetRef.current?.toggleDatePicker();
            }
          }}
          startIcon={{
            icon: pd.icon.main.actions.date_range,
            height: 24,
            width: 24,
            tint: getColor(pd.sys.colour.content_secondary),
          }}
          label={t('flightsSearchPage.main.travelDate')}
          disabled={false}
          editable={false}
          value={getTravelDateDisplay()}
        />
      </View>

      {isWebView && isCenterSheetView ? (
        <DatePickerWithCenterSheet
          key={isOpen ? 'center-sheet-open' : 'center-sheet-closed'}
          ref={datePickerCenterSheetRef}
          width={CENTER_SHEET_WIDTH}
          datePickerProps={{
            activeField: activeField,
            onActiveFieldChange: (val) => {
              setActiveField(val);
            },
            minimumDate: Constants.CALENDAR_START_DATE,
            maximumDate: Constants.CALENDAR_END_DATE,
            isSingleSelection: props.tripType === TripType.ONE_WAY,
            onChange: (dates) => {
              draftDatesRef.current = dates;
              setLocalDates(dates);
              persistSelectedDates(dates);
            },
            selectedDate: localDates,
            showDaysIndicator: true,
            onViewableItems,
            priceValues: isDistress ? undefined : fares,
            unavailablePricePlaceHolder:
              Constants.UNAVAILABLE_PRICE_PLACEHOLDER,
            calendarScrollIndex: calendarScrollIndex,
          }}
          centerSheetViewProps={{
            onCloseIconPress: () => setIsOpen(false),
            onCenterSheetViewClose: () => {
              // handleDatePickerClose clears isDatePickerOpen, not isOpen,
              // and the field's onPress is setIsOpen(true) rather than a
              // toggle - without this the sheet never reopens.
              handleDatePickerClose();
              setIsOpen(false);
            },
          }}
          stickyBottomBarViewProps={{
            supportingText: isDistress
              ? undefined
              : insertParamsInUrl(
                  t('flightsSearchPage.calendar.lowFareLabelDefault'),
                  [],
                ),
            supportingTextStyle: { textAlign: 'center' },
            primaryButtonLabel: t('flightsSearchPage.calendar.ctaConfirm'),
            secondaryButtonLabel: t('flightsSearchPage.calendar.clear'),
            primaryButtonOnPress: () => {
              didConfirmRef.current = true;
              let departDate: Date | undefined;
              let returnDate: Date | undefined;
              if (localDates.length === 0) {
                departDate = lastSelectedDates.current[0];
                returnDate = lastSelectedDates.current[1];
              } else {
                [departDate, returnDate] = draftDatesRef.current;
                [departDate, returnDate] = validateDatesBeforeConfirm(
                  departDate,
                  returnDate,
                );
              }

              sendDateSelectionEvent(departDate, returnDate);
              props.onChangeDepartDate(departDate);
              props.onChangeReturnDate(returnDate);

              const finalDates: (Date | undefined)[] = [departDate, returnDate];
              const didDatesChange = haveDatesChanged(
                initialDatesRef.current,
                finalDates,
              );
              const shouldTriggerAutoSearch =
                didDatesChange || props.shouldAutoSearchOnCalendarConfirm;

              if (shouldTriggerAutoSearch) {
                props?.autoSearch?.({
                  departDate: finalDates[0],
                  returnDate:
                    props.tripType === TripType.ONE_WAY
                      ? undefined
                      : finalDates[1],
                });
              }
              props?.setShouldAutoSearchOnCalendarConfirm?.(false);

              setLocalDates([departDate, returnDate]);
              lastSelectedDates.current = [departDate, returnDate];
              setIsOpen(false);
            },
            secondaryButtonOnPress: () => {
              onResetCalendar();
            },
          }}
        />
      ) : isOpen && isWebView && anchorLayout ? (
        <PortalWrapperView>
          <View
            ref={dropdownRef}
            nativeID="calendar-dropdown-container"
            testID="calendar-dropdown-container"
            style={isPortalUsageEnabled ? portalStyle : dropDownStyle}
          >
            <DatePickerMenu
              activeField={activeField}
              isSingleSelection={props.tripType === TripType.ONE_WAY}
              minimumDate={Constants.CALENDAR_START_DATE}
              maximumDate={Constants.CALENDAR_END_DATE}
              priceValues={isDistress ? undefined : fares}
              selectedDate={selectedDate}
              onViewableItems={onViewableItems}
              calendarScrollIndex={calendarScrollIndex}
              primaryButtonLabel={t('flightsSearchPage.calendar.ctaConfirm')}
              onChange={(dates) => {
                draftDatesRef.current = dates;
                setLocalDates(dates);
                persistSelectedDates(dates);
              }}
              onActiveFieldChange={(val) => {
                setActiveField(val);
              }}
              secondaryButtonLabel={t('flightsSearchPage.calendar.clear')}
              primaryButtonOnPress={() => {
                let [departDate, returnDate] =
                  localDates.length === 0
                    ? lastSelectedDates.current
                    : draftDatesRef.current;
                [departDate, returnDate] = validateDatesBeforeConfirm(
                  departDate,
                  returnDate,
                );
                sendDateSelectionEvent(departDate, returnDate);
                props.onChangeDepartDate(departDate);
                props.onChangeReturnDate(returnDate);

                const finalDates: (Date | undefined)[] = [
                  departDate,
                  returnDate,
                ];
                const didDatesChange = haveDatesChanged(
                  initialDatesRef.current,
                  finalDates,
                );

                const shouldTriggerAutoSearch =
                  didDatesChange || props.shouldAutoSearchOnCalendarConfirm;

                if (shouldTriggerAutoSearch) {
                  const newDates = {
                    departDate: finalDates[0],
                    returnDate:
                      props.tripType === TripType.ONE_WAY
                        ? undefined
                        : finalDates[1],
                  };
                  props?.autoSearch?.(newDates);
                }
                props?.setShouldAutoSearchOnCalendarConfirm?.(false);

                setLocalDates([departDate, returnDate]);
                lastSelectedDates.current = [departDate, returnDate];
                setIsOpen(false);
              }}
              secondaryButtonOnPress={() => {
                onResetCalendar();
              }}
              supportingText={
                isDistress
                  ? undefined
                  : insertParamsInUrl(
                      t('flightsSearchPage.calendar.lowFareLabelDefault'),
                      [],
                    )
              }
              unavailablePricePlaceHolder={
                Constants.UNAVAILABLE_PRICE_PLACEHOLDER
              }
            />
          </View>
        </PortalWrapperView>
      ) : (
        <DatePickerBottomSheetComponent
          //key={`dp-${pickerKey}`}
          ref={datePickerWithBottomSheetRef}
          datePickerProps={{
            activeField: activeField,
            onActiveFieldChange: (val) => {
              setActiveField(val);
            },
            departDateTextFieldViewProps: {
              startIcon: {
                icon: pd.icon.main.actions.date_range,
                height: 24,
                width: 24,
                tint: getColor(pd.sys.colour.content_secondary),
              },
            },
            returnDateTextFieldViewProps: {
              startIcon: {
                icon: pd.icon.main.actions.date_range,
                height: 24,
                width: 24,
                tint: getColor(pd.sys.colour.content_secondary),
              },
            },
            minimumDate: Constants.CALENDAR_START_DATE,
            maximumDate: Constants.CALENDAR_END_DATE,
            isSingleSelection: props.tripType === TripType.ONE_WAY,
            onChange: (dates) => {
              draftDatesRef.current = dates;
              setLocalDates(dates);
              persistSelectedDates(dates);
            },
            selectedDate: localDates,
            showDaysIndicator: true,
            onViewableItems,
            priceValues: isDistress ? undefined : fares,
            unavailablePricePlaceHolder:
              Constants.UNAVAILABLE_PRICE_PLACEHOLDER,
            calendarScrollIndex: calendarScrollIndex,
          }}
          bottomSheetViewProps={{
            initialHeight: datePickerBottomSheetHeight,
            finalHeight: datePickerBottomSheetHeight,
            onChange: (index) => setIsDatePickerOpen(index >= 0),
            children: undefined,
            showHandle: false,
            onCloseIconPress: handleDatePickerClose,
            onBottomSheetClose: handleDatePickerClose,
          }}
          stickyBottomBarViewProps={{
            supportingText: isDistress
              ? undefined
              : insertParamsInUrl(
                  t('flightsSearchPage.calendar.lowFareLabelDefault'),
                  [],
                ),
            supportingTextStyle: { textAlign: 'center' },
            primaryButtonLabel: t('flightsSearchPage.calendar.ctaConfirm'),
            secondaryButtonLabel: t('flightsSearchPage.calendar.clear'),
            primaryButtonOnPress: () => {
              didConfirmRef.current = true;
              let departDate: Date | undefined;
              let returnDate: Date | undefined;
              if (localDates.length === 0) {
                departDate = lastSelectedDates.current[0];
                returnDate = lastSelectedDates.current[1];
              } else {
                [departDate, returnDate] = draftDatesRef.current;
                [departDate, returnDate] = validateDatesBeforeConfirm(
                  departDate,
                  returnDate,
                );
              }

              sendDateSelectionEvent(departDate, returnDate);
              props.onChangeDepartDate(departDate);
              props.onChangeReturnDate(returnDate);
              setLocalDates([departDate, returnDate]);
              lastSelectedDates.current = [departDate, returnDate];
              datePickerWithBottomSheetRef.current?.toggleDatePicker();
            },
            secondaryButtonOnPress: () => {
              onResetCalendar();
            },
          }}
        />
      )}
    </View>
  );
};

export default CalendarView;
